/**
 * PlayerVisual - Pure SVG visual component for rendering player blocks.
 *
 * Renders ALL players as <circle> with label text per documentation spec.
 * (Squares are reserved for future defensive players, not linemen.)
 * Pure visual component - no state or handlers.
 */

import type { BlockVisualProps } from '@/components/blocks/Types'
import type { PlayerBlock } from '@/types/blocks/canvas/Player'
import { useBlockRenderContext } from '@/components/blocks/BlockRenderContext'
import { PLAYER_RADIUS_FEET } from '@/constants'
import { getTextColorForBackground } from '@/utils/ColorUtils'
import { extractPoint } from '@/utils/Block.utils'
import { calculateLabelFontSize, PLAYER_LABEL_FONT_FAMILY } from '@/utils/PlayerLabel.utils'

export function PlayerVisual({ block, animatedPosition }: BlockVisualProps) {
	// Get scale factors and coordinate transform from context (must be called before any early returns)
	const { scale, coordinateTransform } = useBlockRenderContext()

	// Type guard
	if (block.type !== 'player') {
		console.error('PlayerVisual called with non-player block:', block.type)
		return null
	}

	const playerBlock = block as PlayerBlock
	const { properties } = playerBlock
	// Note: isLineman is intentionally not destructured - it controls behavior (delete protection,
	// group movement), NOT visual appearance. All players render as circles per docs.
	const { label, color, isGhost } = properties
	const playerScale = scale?.player ?? 1.0
	const strokeScale = scale?.playerStroke ?? 1.0
	const fontScale = scale?.playerFont ?? 1.0

	// Determine position - use animatedPosition if provided, otherwise extract from properties
	// Apply coordinate transform for field-to-viewBox conversion (Y-flip)
	const basePosition = extractPoint(playerBlock)
	const rawX = animatedPosition && 'x' in animatedPosition
		? animatedPosition.x
		: basePosition?.x ?? 0
	const rawY = animatedPosition && 'y' in animatedPosition
		? animatedPosition.y
		: basePosition?.y ?? 0
	const transformed = coordinateTransform({ x: rawX, y: rawY })
	const x = transformed.x
	const y = transformed.y

	// Calculate sizes
	const radius = PLAYER_RADIUS_FEET * playerScale
	// Ghost players have double stroke width for visibility
	const strokeWidth = (isGhost ? 0.3 : 0.15) * strokeScale
	// Use intelligent font sizing that fills the bubble based on label length
	const fontSize = calculateLabelFontSize(label, radius, fontScale)

	// Calculate text color for visibility (only needed for non-ghost players)
	const textColor = isGhost ? 'transparent' : getTextColorForBackground(color)

	// All players render as circles per documentation spec
	// (isLineman controls behavior like delete protection and group movement, NOT visual shape)
	// Ghost rendering: transparent fill, dashed stroke, no label (matches BasePlayer.tsx)
	return (
		<g opacity={isGhost ? 0.4 : 1}>
			<circle
				cx={x}
				cy={y}
				r={radius}
				fill={isGhost ? 'transparent' : color}
				stroke="var(--foreground)"
				strokeWidth={strokeWidth}
				strokeDasharray={isGhost ? '0.6,0.3' : undefined}
			/>
			{!isGhost && (
				<text
					x={x}
					y={y}
					fill={textColor}
					fontSize={fontSize}
					fontFamily={PLAYER_LABEL_FONT_FAMILY}
					fontWeight="bold"
					textAnchor="middle"
					dy="0.35em"
					style={{ userSelect: 'none' }}
				>
					{label}
				</text>
			)}
		</g>
	)
}
